import { AlertColor, Box } from '@mui/material';
import UPButton from 'components/button';
import { ReactNode, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GENERAL__RETRY } from 'translations/constants';

export interface Toast {
  id: number;
  type: AlertColor;
  content: ReactNode;
}

export const useToasts = () => {
  const [t] = useTranslation();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextToastId = useRef(0);

  const pushToast = useCallback((type: AlertColor, content: ReactNode): number => {
    nextToastId.current += 1;
    const id = nextToastId.current;
    setToasts(previous => [...previous, { id, type, content }]);
    return id;
  }, []);

  const dismissToast = useCallback(
    (id: number) => setToasts(previous => previous.filter(toast => toast.id !== id)),
    [],
  );

  const pushRetryToast = useCallback(
    (message: string, retry: () => void, retryTestId: string) => {
      let toastId = 0;
      const onRetry = () => {
        dismissToast(toastId);
        retry();
      };
      toastId = pushToast(
        'error',
        <Box display="flex" alignItems="center" gap="12px">
          <span>{message}</span>
          <UPButton text={t(GENERAL__RETRY)} onClick={onRetry} outlined testId={retryTestId} />
        </Box>,
      );
    },
    [dismissToast, pushToast, t],
  );

  return { toasts, pushToast, dismissToast, pushRetryToast };
};
